// In-app notifications on the website: list, unread count, read state and preferences.
export const categoryLabels = {
  bids: ['Bids', 'New and updated bids on your jobs'],
  awards: ['Chosen bids', 'When your bid is chosen or not chosen'],
  messages: ['Messages', 'New messages about your jobs'],
  visits: ['Visits', 'Proposed, confirmed and cancelled visits'],
  repairs: ['Repairs', 'Updates on repair reports'],
  work_updates: ['Work updates', 'Progress, completion and reviews'],
  payments: ['Payments', 'Invoices, payments and refunds'],
  saved_searches: ['Saved searches', 'New work that matches your searches'],
  account: ['Account and safety', 'Always on so you never miss something important'],
};

const errorText = {
  SIGN_IN_REQUIRED: 'Sign in to see your notifications.',
  NOTIFICATION_CATEGORY_LOCKED: 'Account and safety notifications cannot be turned off.',
  INVALID_NOTIFICATION_CATEGORY: 'That notification setting is not available.',
  INVALID_NOTIFICATION_PREFERENCE: 'That notification setting is not available.',
};

async function call(client, fn, args) {
  if (!client) throw new Error('Notifications are not connected. Please try again later.');
  const { data, error } = await client.rpc(fn, args);
  if (error) {
    const code = Object.keys(errorText).find(key => String(error.message || '').includes(key));
    throw new Error(code ? errorText[code] : 'Notifications could not be loaded. Please try again.');
  }
  return data;
}

export async function listNotifications(client, { before = null, limit = 30, includeArchived = false } = {}) {
  const page = await call(client, 'list_my_notifications', {
    p_before_created_at: before?.created_at ?? null, p_before_id: before?.id ?? null, p_limit: limit, p_include_archived: includeArchived,
  });
  return { items: page?.items || [], hasMore: Boolean(page?.has_more) };
}
export const unreadNotificationCount = async client => Number(await call(client, 'unread_notification_count')) || 0;
export const markNotificationsRead = async (client, ids = null) => Number(await call(client, 'mark_notifications_read', { p_ids: ids })) || 0;
export const archiveNotifications = async (client, ids) => Number(await call(client, 'archive_notifications', { p_ids: ids })) || 0;
export const getNotificationPreferences = async client => (await call(client, 'get_notification_preferences')) || [];
export const setNotificationPreference = async (client, { category, push, email, in_app }) =>
  (await call(client, 'set_notification_preference', { p_category: category, p_push: push, p_email: email, p_in_app: in_app })) || [];

const uuid = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

/** Notification paths are written for the app; map them to the matching website page. */
export function webPathFor(data) {
  const path = typeof data?.path === 'string' ? data.path : '';
  if (!path || path.includes('..') || path.includes('//')) return '/notifications';
  if (new RegExp(`^/messages(/${uuid})?$`, 'i').test(path)) return path;
  if (new RegExp(`^/work(/${uuid})?$`, 'i').test(path)) return path;
  const managed = new RegExp(`^/work/manage/(home_job|job|tender)/${uuid}$`, 'i').exec(path);
  if (managed) return managed[1] === 'home_job' ? '/home/jobs' : '/commercial/manage';
  if (new RegExp(`^/resident/repairs/${uuid}$`, 'i').test(path)) return '/repairs';
  if (path === '/housing-provider/repairs' || path === '/appeal') return path;
  if (/^\/supplier\//.test(path)) return '/supplier/opportunities';
  return '/notifications';
}

export function notificationTime(iso, now = new Date()) {
  const at = new Date(iso);
  const seconds = Math.max(0, Math.round((now.getTime() - at.getTime()) / 1000));
  if (Number.isNaN(seconds)) return '';
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`;
  const days = Math.floor(seconds / 86400);
  if (days < 7) return days === 1 ? '1 day ago' : `${days} days ago`;
  return at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: at.getFullYear() === now.getFullYear() ? undefined : 'numeric' });
}

// A tiny shared unread count so the header bell and the page agree.
let unread = null;
const listeners = new Set();
export function setUnread(value) { unread = value; listeners.forEach(listener => listener(value)); }
export function subscribeUnread(listener) { listeners.add(listener); listener(unread); return () => listeners.delete(listener); }
export async function refreshUnread(client) {
  try { const value = await unreadNotificationCount(client); setUnread(value); return value; } catch { return unread; }
}
